/**
 * Hook para busca contextual de mensagens dentro de uma conversa.
 * - Chama a RPC `search_conversation_messages` (server-side).
 * - Suporta paginação infinita (10 por página).
 * - Filtros: all | inbound | outbound | media.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export type MessageSearchFilter = 'all' | 'inbound' | 'outbound' | 'media';

export interface ConversationSearchResult {
  id: string;
  conversation_id: string;
  sender_type: string;
  sender_id: string | null;
  sender_name: string | null;
  sender_avatar: string | null;
  content: string | null;
  snippet: string | null;
  message_type: string;
  direction: string;
  sent_at: string;
  total_count: number;
}

const PAGE_SIZE = 10;

interface Params {
  conversationId: string | undefined;
  query: string;
  filter: MessageSearchFilter;
  enabled: boolean;
}

interface SearchPage {
  items: ConversationSearchResult[];
  total: number;
}

export function useConversationMessageSearch({ conversationId, query, filter, enabled }: Params) {
  return useInfiniteQuery<SearchPage, Error>({
    queryKey: ['conversation-message-search', conversationId, query.trim(), filter],
    enabled: enabled && !!conversationId && query.trim().length >= 1,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      const total = lastPage.total ?? 0;
      return loaded < total ? loaded : undefined;
    },
    queryFn: async ({ pageParam = 0 }) => {
      const { data, error } = await supabase.rpc('search_conversation_messages', {
        p_conversation_id: conversationId!,
        p_query: query.trim(),
        p_filter: filter,
        p_limit: PAGE_SIZE,
        p_offset: pageParam as number,
      });

      if (error) {
        logger.error('[useConversationMessageSearch] Erro ao buscar mensagens', error.message);
        throw new Error(error.message);
      }

      const rows = (data ?? []) as ConversationSearchResult[];
      return {
        items: rows,
        total: rows[0]?.total_count ? Number(rows[0].total_count) : 0,
      };
    },
    staleTime: 30_000,
  });
}

